import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioNotificaciones,
  type PoliticaEscalamiento,
} from '../../../data/repositories/RepositorioNotificaciones';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import {
  Criticidad,
  CRITICIDADES,
  type CriticidadValor,
} from '../../../domain/value-objects/Criticidad';
import { CadenaEscalamiento } from '../../../domain/policies/CadenaEscalamiento';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import estilos from '../../components/layout/PaginaCrud.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

const CAMPOS_VACIOS = {
  clienteId: '',
  nivel: '1',
  severidad: 'media' as CriticidadValor,
  activo: true,
  destinatarios: [] as number[],
};

/**
 * Cadenas de escalamiento.
 *
 * Cada nivel dice a quién avisar y a partir de qué gravedad. Los niveles deben
 * ser consecutivos: si existe el 3 pero falta el 2, el aviso nunca llega a
 * ese tercer nivel. La pantalla detecta y señala esos huecos, que en la
 * versión anterior había que ir a buscar a mano.
 */
export function Escalamiento() {
  const clienteConsultas = useQueryClient();
  const politicaAcceso = useSesionStore((s) => s.politica);
  const puedeGestionar = politicaAcceso?.puede('gestionar_notificaciones') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar el escalamiento';

  const [clienteFiltro, setClienteFiltro] = useState<number | null>(null);

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<PoliticaEscalamiento | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<PoliticaEscalamiento | null>(null);
  const [campos, setCampos] = useState(CAMPOS_VACIOS);
  const [error, setError] = useState<string | null>(null);

  const politicas = useQuery({
    queryKey: ['escalamiento', clienteFiltro],
    queryFn: () => repositorioNotificaciones.listarPoliticas(clienteFiltro),
  });

  const contactos = useQuery({
    queryKey: ['contactos', null],
    queryFn: () => repositorioNotificaciones.listarContactos(null),
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['escalamiento'] });
  };

  const guardar = useMutation({
    mutationFn: () => {
      const datos = {
        cliente_id: Number(campos.clienteId),
        nivel_escalacion: Number(campos.nivel),
        severidad_minima: campos.severidad,
        activo: campos.activo,
      };

      return enEdicion
        ? repositorioNotificaciones.actualizarPolitica(enEdicion.id, datos, campos.destinatarios)
        : repositorioNotificaciones.crearPolitica(datos, campos.destinatarios);
    },
    onSuccess: () => {
      toast.success(enEdicion ? 'Nivel actualizado' : 'Nivel creado');
      setAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('escalamiento.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el nivel');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => repositorioNotificaciones.eliminarPolitica(id),
    onSuccess: () => {
      toast.success('Nivel eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError('escalamiento.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar el nivel');
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      repositorioNotificaciones.cambiarActivoPolitica(id, activo),
    onSuccess: refrescar,
    onError: () => toast.error('No se pudo cambiar el estado del nivel'),
  });

  const lista = useMemo(() => politicas.data ?? [], [politicas.data]);

  /** Fallos de la cadena, con el nombre del cliente ya resuelto. */
  const fallos = useMemo(() => {
    const cadena = new CadenaEscalamiento(
      lista.map((p) => ({
        clienteId: p.clienteId,
        nivel: p.nivel,
        activo: p.activo,
        destinatarios: p.destinatarios.length,
      })),
    );

    return cadena.detectarFallos().map((f) => {
      const nombre =
        clientes.data?.find((c) => c.id === f.clienteId)?.nombre ?? `Cliente ${f.clienteId}`;
      return `${nombre}: ${f.descripcion}`;
    });
  }, [lista, clientes.data]);

  const abrirNuevo = () => {
    setEnEdicion(null);
    setCampos(CAMPOS_VACIOS);
    setError(null);
    setAbierto(true);
  };

  const abrirEdicion = (p: PoliticaEscalamiento) => {
    setEnEdicion(p);
    setCampos({
      clienteId: String(p.clienteId ?? ''),
      nivel: String(p.nivel),
      severidad: p.severidadMinima.valor,
      activo: p.activo,
      destinatarios: p.destinatarios.map((d) => d.id),
    });
    setError(null);
    setAbierto(true);
  };

  const enviar = () => {
    setError(null);

    if (!campos.clienteId) {
      setError('Elige a qué cliente se aplica este nivel.');
      return;
    }

    const nivel = Number(campos.nivel);
    if (!Number.isInteger(nivel) || nivel < 1) {
      setError('El nivel debe ser un número entero mayor que cero.');
      return;
    }
    if (campos.destinatarios.length === 0) {
      setError('Elige al menos un contacto al que avisar en este nivel.');
      return;
    }

    guardar.mutate();
  };

  /** Contactos del cliente elegido: avisar a alguien de otro no tiene sentido. */
  const contactosDelCliente = useMemo(
    () =>
      (contactos.data ?? []).filter(
        (c) => !campos.clienteId || c.clienteId === Number(campos.clienteId),
      ),
    [contactos.data, campos.clienteId],
  );

  const columnas: Columna<PoliticaEscalamiento>[] = [
    {
      clave: 'nivel',
      encabezado: 'Nivel',
      movil: 'titulo',
      render: (p) => <span className={estilos.nombre}>Nivel {p.nivel}</span>,
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      movil: 'subtitulo',
      render: (p) => {
        const nombre = clientes.data?.find((c) => c.id === p.clienteId)?.nombre;
        return nombre ?? '—';
      },
    },
    {
      clave: 'severidad',
      encabezado: 'Se activa desde',
      render: (p) => <Badge color={p.severidadMinima.color}>{p.severidadMinima.etiqueta}</Badge>,
    },
    {
      clave: 'destinatarios',
      encabezado: 'Avisa a',
      render: (p) =>
        p.destinatarios.length === 0 ? (
          <Badge color="var(--status-danger)" title="Este nivel no avisará a nadie">
            Sin destinatarios
          </Badge>
        ) : (
          <span title={p.destinatarios.map((d) => d.nombre).join(', ')}>
            {p.destinatarios.length === 1
              ? p.destinatarios[0]?.nombre
              : `${p.destinatarios.length} contactos`}
          </span>
        ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (p) =>
        p.activo ? (
          <Badge color="var(--status-success)">Activo</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactivo</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (p) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={p.activo ? 'interruptorOn' : 'interruptorOff'}
            title={p.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={sinPermiso}
            onClick={() => alternar.mutate({ id: p.id, activo: !p.activo })}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(p)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => abrirEdicion(p)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => {
              const seguro = window.confirm(
                `¿Eliminar el nivel ${p.nivel}?\n\n` +
                  'Si quedan niveles por encima, la cadena de avisos se cortará en este punto.',
              );
              if (seguro) eliminar.mutate(p.id);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Escalamiento"
        descripcion="A quién se avisa en cada nivel, según la gravedad de la alarma."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={politicas.isFetching}
            >
              Actualizar
            </Button>
            <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
              Nuevo nivel
            </Button>
          </>
        }
      />

      {fallos.length > 0 && (
        <div className={estilos.error} style={{ marginBottom: '0.75rem' }} role="status">
          <Icon nombre="advertenciaContorno" tamano={16} />
          <span>Revisa la cadena de avisos: {fallos.join(' · ')}.</span>
        </div>
      )}

      <BarraFiltros>
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteFiltro ?? ''}
          onChange={(e) => setClienteFiltro(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
      </BarraFiltros>

      {politicas.isLoading ? (
        <ListaEsqueleto filas={4} />
      ) : politicas.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : lista.length === 0 ? (
        <EstadoVacio
          icono="escalamiento"
          titulo="Sin niveles de escalamiento"
          descripcion="Define al menos un nivel para que las alarmas lleguen a alguien."
          acciones={
            <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
              Crear el primero
            </Button>
          }
        />
      ) : (
        <>
          <Tabla
            datos={lista}
            columnas={columnas}
            claveFila={(p) => p.id}
            etiqueta="Niveles de escalamiento"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Política de escalamiento"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <Modal
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        titulo={enEdicion ? `Editar nivel ${enEdicion.nivel}` : 'Nuevo nivel de escalamiento'}
        descripcion="Los niveles se recorren en orden: el 1 avisa primero, el 2 después."
        pie={
          <>
            <Button variante="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={enviar} cargando={guardar.isPending} icono="guardar">
              Guardar
            </Button>
          </>
        }
      >
        <div className={estilos.formulario}>
          {error && (
            <div className={estilos.error}>
              <Icon nombre="errorContorno" tamano={15} />
              {error}
            </div>
          )}

          <Select
            etiqueta="Cliente"
            requerido
            placeholder="Elige un cliente"
            value={campos.clienteId}
            onChange={(e) =>
              setCampos((c) => ({ ...c, clienteId: e.target.value, destinatarios: [] }))
            }
            opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
          />

          <Input
            etiqueta="Nivel"
            type="number"
            min={1}
            requerido
            value={campos.nivel}
            onChange={(e) => setCampos((c) => ({ ...c, nivel: e.target.value }))}
            ayuda="El 1 es el primero en avisarse; los siguientes entran si nadie atiende"
          />

          <Select
            etiqueta="Gravedad mínima"
            value={campos.severidad}
            onChange={(e) =>
              setCampos((c) => ({ ...c, severidad: e.target.value as CriticidadValor }))
            }
            ayuda="Este nivel sólo se activa con alarmas de esta gravedad o superior"
            opciones={CRITICIDADES.map((c) => ({
              valor: c,
              etiqueta: Criticidad.from(c).etiqueta,
            }))}
          />

          <div>
            <span
              style={{
                display: 'block',
                fontSize: 'var(--text-label)',
                fontWeight: 700,
                color: 'var(--text-2)',
                marginBottom: '0.4rem',
              }}
            >
              A quién avisar
            </span>

            {contactosDelCliente.length === 0 ? (
              <p className={estilos.ayuda}>
                {campos.clienteId
                  ? 'Ese cliente no tiene contactos de alerta. Créalos primero.'
                  : 'Elige un cliente para ver sus contactos.'}
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {contactosDelCliente.map((c) => (
                  <label key={c.id} className={estilos.opcion}>
                    <input
                      type="checkbox"
                      checked={campos.destinatarios.includes(c.id)}
                      onChange={(e) =>
                        setCampos((prev) => ({
                          ...prev,
                          destinatarios: e.target.checked
                            ? [...prev.destinatarios, c.id]
                            : prev.destinatarios.filter((id) => id !== c.id),
                        }))
                      }
                    />
                    <span>
                      <strong>{c.nombre}</strong>
                      <span className={estilos.ayuda}>
                        {[c.cargo, c.email ?? c.whatsapp ?? c.telegram].filter(Boolean).join(' · ')}
                        {!c.activo && ' · inactivo, no recibirá avisos'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.activo}
              onChange={(e) => setCampos((c) => ({ ...c, activo: e.target.checked }))}
            />
            <span>
              <strong>Nivel activo</strong>
              <span className={estilos.ayuda}>
                Si se desactiva, se salta en la cadena de avisos
              </span>
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
